import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api';

export default function JenkinsConfig() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const [jobName, setJobName] = useState('');
  const [buildNumber, setBuildNumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [buildStatus, setBuildStatus] = useState(null);
  const [testResults, setTestResults] = useState(null);

  async function checkBuildStatus() {
    if (!jobName || !buildNumber) {
      setError('Job name and build number are required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/jenkins/status/${encodeURIComponent(jobName)}/${buildNumber}`);
      setBuildStatus(res.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to fetch build status');
    } finally {
      setLoading(false);
    }
  }

  async function fetchResults() {
    if (!jobName || !buildNumber) {
      setError('Job name and build number are required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/jenkins/results/${encodeURIComponent(jobName)}/${buildNumber}`);
      setTestResults(res.data);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'Failed to fetch test results');
    } finally {
      setLoading(false);
    }
  }

  function getStatusBadge(status) {
    const statusMap = {
      SUCCESS: 'bg-success',
      FAILURE: 'bg-danger',
      UNSTABLE: 'bg-warning',
      ABORTED: 'bg-secondary',
      BUILDING: 'bg-info',
    };
    return statusMap[status] || 'bg-secondary';
  }

  return (
    <div>
      <h2>Jenkins Integration</h2>

      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Configuration</h5>
        </div>
        <div className="card-body">
          <div className="alert alert-info">
            <strong>Setup Instructions:</strong>
            <ol className="mb-0">
              <li>
                Set <code>JENKINS_URL</code>, <code>JENKINS_USER</code>, and{' '}
                <code>JENKINS_TOKEN</code> in backend <code>.env</code>
              </li>
              <li>Install Jenkins Notification Plugin</li>
              <li>
                Configure webhook: <code>POST http://your-server:4000/api/jenkins/webhook</code>
              </li>
              <li>
                Webhook payload:{' '}
                <code>
                  {
                    '{ "jobName": "$JOB_NAME", "buildNumber": $BUILD_NUMBER, "status": "$STATUS", "result": "$BUILD_RESULT" }'
                  }
                </code>
              </li>
            </ol>
          </div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Check Build Status</h5>
        </div>
        <div className="card-body">
          <div className="row g-3 mb-3">
            <div className="col-md-5">
              <input
                type="text"
                className="form-control"
                placeholder="Job name (e.g., my-test-job)"
                value={jobName}
                onChange={e => setJobName(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="col-md-3">
              <input
                type="number"
                className="form-control"
                placeholder="Build #"
                value={buildNumber}
                onChange={e => setBuildNumber(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="col-md-4">
              <button
                className="btn btn-primary me-2"
                onClick={checkBuildStatus}
                disabled={loading}
              >
                {loading ? 'Checking...' : 'Check Status'}
              </button>
              <button className="btn btn-secondary" onClick={fetchResults} disabled={loading}>
                {loading ? 'Fetching...' : 'Fetch Results'}
              </button>
            </div>
          </div>

          {buildStatus && (
            <div className="alert alert-light">
              <h6>Build Status:</h6>
              <p>
                <span className={`badge ${getStatusBadge(buildStatus.status)}`}>
                  {buildStatus.status}
                </span>{' '}
                {buildStatus.building && <span className="badge bg-info">Building</span>}
              </p>
              <p className="mb-1">
                <strong>Result:</strong> {buildStatus.result || 'N/A'}
              </p>
              <p className="mb-1">
                <strong>Duration:</strong> {(buildStatus.duration / 1000).toFixed(1)}s
              </p>
              {buildStatus.url && (
                <p className="mb-0">
                  <strong>URL:</strong>{' '}
                  <a href={buildStatus.url} target="_blank" rel="noopener noreferrer">
                    {buildStatus.url}
                  </a>
                </p>
              )}
            </div>
          )}

          {testResults && (
            <div className="mt-3">
              <h6>Test Results ({testResults.count} tests):</h6>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th>Test Name</th>
                      <th>Status</th>
                      <th>Duration</th>
                      <th>Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testResults.results.map((result, idx) => (
                      <tr key={idx}>
                        <td>
                          <small>{result.className}</small>
                          <br />
                          <strong>{result.name}</strong>
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              result.status === 'passed'
                                ? 'bg-success'
                                : result.status === 'failed'
                                  ? 'bg-danger'
                                  : result.status === 'skipped'
                                    ? 'bg-warning'
                                    : 'bg-secondary'
                            }`}
                          >
                            {result.status}
                          </span>
                        </td>
                        <td>{(result.duration / 1000).toFixed(2)}s</td>
                        <td>
                          {result.message && (
                            <small className="text-muted">{result.message.substring(0, 100)}</small>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
